import { useEffect, useState } from "react";
import useRetryFetch from "../../hooks/useRetryFetch";
import { config } from "../../Environment";
import ReactMarkdown from "react-markdown";
import { Button } from "../ui/button";
import { GeneratingLoader } from "../ui/generating-loader";

const AIPrompt = ({ prompt, componentKey, setUserData, settings = {} }) => {
  const [completion, setCompletion] = useState("");
  const [isTriggered, setIsTriggered] = useState(false);
  const retryFetch = useRetryFetch(prompt);

  // Destructure settings with defaults
  const {
    responseFormat = "plain", // 'plain', 'markdown', 'richtext'
    deferredExecution = false,
    triggerButtonText = "Generate Response",
    preButtonMessage = "Click the button below to generate the AI response.",
  } = settings;

  useEffect(() => {
    // Only execute if not deferred, or if deferred and triggered
    if (deferredExecution && !isTriggered) {
      return;
    }

    const getCompletion = async () => {
      const response = await retryFetch(
        `${config.API_BASE}/api/ai/completion`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            prompt: prompt,
            responseFormat: responseFormat,
            enhancedStyling: settings.enhancedStyling || false,
          }),
        }
      );
      const data = await response.json();
      setUserData((oldUserData) => {
        let newUserData = { ...oldUserData };
        newUserData[componentKey] = data.text;
        return newUserData;
      });
      setCompletion(data.text);
    };
    getCompletion();
  }, [prompt, isTriggered, responseFormat]);

  // If deferred execution and not triggered, show button
  if (deferredExecution && !isTriggered) {
    return (
      <div className="mt-4 text-start">
        {preButtonMessage && (
          <p className="mb-4 text-gray-700">{preButtonMessage}</p>
        )}
        <Button onClick={() => setIsTriggered(true)}>
          {triggerButtonText}
        </Button>
      </div>
    );
  }

  // Render based on response format
  const renderCompletion = () => {
    if (!completion) {
      return <GeneratingLoader context="prompt" />;
    }

    if (responseFormat === "markdown") {
      return (
        <div className="mt-4 prose prose-sm max-w-none">
          <ReactMarkdown>{completion}</ReactMarkdown>
        </div>
      );
    }

    if (responseFormat === "richtext") {
      return (
        <div
          className="mt-4 prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: completion }}
        ></div>
      );
    }

    // Default plain text rendering
    return <div className="mt-4 whitespace-pre-wrap">{completion}</div>;
  };

  return <div className="mt-4 text-start">{renderCompletion()}</div>;
};

export default AIPrompt;
